import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import blogPosts from '../data/blogPosts';

const categories = ['All', ...new Set(blogPosts.map((post) => post.category))];

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredPosts =
    activeCategory === 'All'
      ? blogPosts
      : blogPosts.filter((post) => post.category === activeCategory);

  // Sort all posts by date descending so newest appear first
  const sortedPosts = [...filteredPosts].sort((a, b) => new Date(b.date) - new Date(a.date));
  const featuredPost = sortedPosts[0];
  const remainingPosts = sortedPosts.slice(1);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Moving Blog – Expert Tips, Costs & Guides | MyMoveAdvisor</title>
        <meta
          name="description"
          content="Read expert moving tips, cost guides, real mover stories, and relocation advice. Everything Americans need to know about hiring movers and planning a stress-free move."
        />
        <meta
          name="keywords"
          content="moving blog, moving tips, moving costs 2026, how to hire movers, moving checklist, long distance moving guide, moving scams, packing tips"
        />
        <link rel="canonical" href="https://mymoveadvisor.com/blog" />
        <meta
          property="og:title"
          content="Moving Blog – Expert Tips & Guides | MyMoveAdvisor"
        />
        <meta
          property="og:description"
          content="Expert moving tips, cost breakdowns, and real stories from verified movers across the US."
        />
        <meta property="og:url" content="https://mymoveadvisor.com/blog" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Dark Hero Header */}
      <div className="bg-gray-900 border-b border-white/10 pt-32 pb-20 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-primary/5 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <nav className="flex mb-8 text-sm font-medium">
              <Link
                to="/"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Home
              </Link>
              <span className="text-gray-600 mx-3">/</span>
              <span className="text-primary">Blog</span>
            </nav>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
              Moving <span className="text-primary">Insights</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              Expert guides, real stories, cost breakdowns, and everything you
              need to plan a stress-free move across the United States.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-3 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                activeCategory === cat
                  ? 'bg-primary text-white shadow-lg shadow-primary/25'
                  : 'bg-white text-gray-600 hover:text-primary border border-gray-200 hover:border-primary/30'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Featured Post — only on "All" view */}
        {activeCategory === 'All' && featuredPost && (
          <Link
            to={`/blog/${featuredPost.slug}`}
            className="block mb-12 group"
          >
            <article className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:shadow-[0_20px_50px_rgb(0,0,0,0.1)] transition-all duration-500 hover:-translate-y-1">
              <div className="grid md:grid-cols-2">
                <div className="relative h-64 md:h-auto overflow-hidden">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="bg-primary text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider">
                      Featured
                    </span>
                    <span className="bg-white/90 backdrop-blur-sm text-gray-700 text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider">
                      {featuredPost.category}
                    </span>
                  </div>
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 text-sm text-gray-400 mb-4">
                    <span>{featuredPost.date}</span>
                    <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                    <span>{featuredPost.readTime}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors duration-300 leading-tight">
                    {featuredPost.title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center text-primary font-semibold text-sm">
                    Read Full Article
                    <svg
                      className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </article>
          </Link>
        )}

        {/* Blog Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {(activeCategory === 'All' ? remainingPosts.filter((p) => p.id !== featuredPost?.id) : remainingPosts).map(
            (post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group block"
              >
                <article className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-[0_15px_40px_rgb(0,0,0,0.08)] transition-all duration-500 hover:-translate-y-1 h-full flex flex-col">
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-white/90 backdrop-blur-sm text-gray-700 text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                      <span>{post.date}</span>
                      <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                      <span>{post.readTime}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors duration-300 leading-snug line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-grow line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center text-primary font-semibold text-sm mt-auto pt-4 border-t border-gray-100">
                      Read More
                      <svg
                        className="w-4 h-4 ml-1.5 group-hover:translate-x-1.5 transition-transform duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        />
                      </svg>
                    </div>
                  </div>
                </article>
              </Link>
            )
          )}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">📝</div>
            <p className="text-gray-500 text-lg">
              No posts in this category yet. Check back soon!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
